/**
 * Hover documentation for Nextflow DSL2 constructs.
 * Values are Markdown strings rendered in the Monaco hover widget.
 */
export const nextflowHoverData: Record<string, string> = {
  process: [
    "**process** — Nextflow DSL2",
    "",
    "Defines a computational step in the pipeline with `input`, `output`, and `script` blocks.",
    "",
    "```groovy",
    "process FASTQC {",
    "  input:",
    "    path reads",
    "  output:",
    '    path "*.html"',
    "  script:",
    '    """',
    "    fastqc ${reads}",
    '    """',
    "}",
    "```",
  ].join("\n"),

  workflow: [
    "**workflow** — Nextflow DSL2",
    "",
    "Defines a workflow that composes processes and sub-workflows through channels.",
    "",
    "```groovy",
    "workflow {",
    "  FASTQC(read_pairs_ch)",
    "  MULTIQC(FASTQC.out.collect())",
    "}",
    "```",
  ].join("\n"),

  channel: [
    "**channel** — Nextflow",
    "",
    "A queue that connects process inputs and outputs. Create with factory methods:",
    "",
    "```groovy",
    'Channel.fromPath("/data/*.fastq")',
    "Channel.of(1, 2, 3)",
    "Channel.value(42)",
    'Channel.fromFilePairs("/data/*_{1,2}.fastq")',
    "```",
  ].join("\n"),

  Channel: [
    "**Channel** — Nextflow",
    "",
    "Factory class for creating channels.",
    "",
    "```groovy",
    "Channel.of(1, 2, 3)",
    'Channel.fromPath("/data/*.fastq")',
    'Channel.fromFilePairs("/data/*_{1,2}.fastq")',
    'Channel.fromSRA("SRP043510")',
    "Channel.value(42)",
    "Channel.empty()",
    "```",
  ].join("\n"),

  params: [
    "**params** — Nextflow",
    "",
    "Immutable map holding pipeline parameters. Defined in `nextflow.config` or via CLI `--paramName`.",
    "",
    "```groovy",
    'params.reads = "/data/*.fastq"',
    'params.outdir = "./results"',
    "",
    "// Usage in process:",
    "path(params.reads)",
    "```",
  ].join("\n"),

  input: [
    "**input:** — Nextflow process block",
    "",
    "Declares the expected input channels for a process. Qualifiers: `val`, `path`, `env`, `stdin`, `tuple`, `each`.",
    "",
    "```groovy",
    "input:",
    "  val(sample_id)",
    "  path(reads)",
    "  tuple val(id), path(fastq)",
    "```",
  ].join("\n"),

  output: [
    "**output:** — Nextflow process block",
    "",
    "Declares the output channels emitted by a process. Qualifiers: `val`, `path`, `env`, `stdout`, `tuple`.",
    "",
    "```groovy",
    "output:",
    '  path "*.bam", emit: aligned',
    '  path "*.log"',
    '  tuple val(id), path("*.vcf")',
    "```",
  ].join("\n"),

  script: [
    "**script:** — Nextflow process block",
    "",
    "Defines the command to execute. Supports Groovy string interpolation with `${var}`.",
    "",
    "```groovy",
    "script:",
    '  """',
    "  bwa mem ${genome} ${reads} > ${id}.bam",
    '  """',
    "```",
  ].join("\n"),

  shell: [
    "**shell:** — Nextflow process block",
    "",
    "Like `script:` but uses `!{var}` for Nextflow variables and `$var` for Bash variables.",
    "",
    "```groovy",
    "shell:",
    "  '''",
    "  echo $HOSTNAME",
    "  bwa mem !{genome} !{reads} > !{id}.bam",
    "  '''",
    "```",
  ].join("\n"),

  exec: [
    "**exec:** — Nextflow process block",
    "",
    "Executes native Groovy code instead of a system command.",
    "",
    "```groovy",
    "exec:",
    '  result = "Hello $name"',
    "```",
  ].join("\n"),

  when: [
    "**when:** — Nextflow process block",
    "",
    "Conditional guard — the process only executes when the expression evaluates to `true`.",
    "",
    "```groovy",
    "when:",
    '  sample_id != "control"',
    "```",
  ].join("\n"),

  stub: [
    "**stub:** — Nextflow process block",
    "",
    "Defines a lightweight mock command for pipeline testing (`-stub-run`).",
    "",
    "```groovy",
    "stub:",
    '  """',
    "  touch ${id}.bam",
    '  """',
    "```",
  ].join("\n"),

  path: [
    "**path** — Nextflow input/output qualifier",
    "",
    "Declares a file or directory input/output.",
    "",
    "```groovy",
    "input:",
    "  path reads          // single file",
    '  path "*.fastq"      // glob pattern (output)',
    "```",
  ].join("\n"),

  val: [
    "**val** — Nextflow input/output qualifier",
    "",
    "Declares a value (non-file) input/output.",
    "",
    "```groovy",
    "input:",
    "  val sample_id",
    "output:",
    '  val "${count}"',
    "```",
  ].join("\n"),

  tuple: [
    "**tuple** — Nextflow input/output qualifier",
    "",
    "Groups multiple input/output values together.",
    "",
    "```groovy",
    "input:",
    "  tuple val(id), path(reads)",
    "output:",
    '  tuple val(id), path("*.bam")',
    "```",
  ].join("\n"),

  each: [
    "**each** — Nextflow input qualifier",
    "",
    "Repeats the process execution for each item in the collection.",
    "",
    "```groovy",
    "input:",
    "  each mode",
    "  path reads",
    "```",
  ].join("\n"),

  emit: [
    "**emit** — Nextflow",
    "",
    "Names an output channel for referencing in workflows.",
    "",
    "```groovy",
    "output:",
    '  path "*.bam", emit: aligned',
    "",
    "// In workflow:",
    "ALIGN.out.aligned",
    "```",
  ].join("\n"),

  publishDir: [
    "**publishDir** — Nextflow process directive",
    "",
    "Copies output files to a specified directory.",
    "",
    "```groovy",
    'publishDir "${params.outdir}/results", mode: "copy"',
    'publishDir "${params.outdir}/logs", pattern: "*.log"',
    "```",
  ].join("\n"),

  container: [
    "**container** — Nextflow process directive",
    "",
    "Specifies the Docker/Singularity container image for the process.",
    "",
    "```groovy",
    'container "biocontainers/fastqc:0.11.9"',
    "```",
  ].join("\n"),

  cpus: [
    "**cpus** — Nextflow process directive",
    "",
    "Sets the number of CPUs allocated to the process.",
    "",
    "```groovy",
    "cpus 4",
    "```",
  ].join("\n"),

  memory: [
    "**memory** — Nextflow process directive",
    "",
    "Sets the maximum memory allocated to the process.",
    "",
    "```groovy",
    "memory '8 GB'",
    "```",
  ].join("\n"),

  time: [
    "**time** — Nextflow process directive",
    "",
    "Sets the maximum execution time for the process.",
    "",
    "```groovy",
    "time '2h'",
    "```",
  ].join("\n"),

  errorStrategy: [
    "**errorStrategy** — Nextflow process directive",
    "",
    "Defines the action on process failure: `terminate`, `finish`, `ignore`, `retry`.",
    "",
    "```groovy",
    "errorStrategy 'retry'",
    "maxRetries 3",
    "```",
  ].join("\n"),

  maxRetries: [
    "**maxRetries** — Nextflow process directive",
    "",
    "Maximum number of times the process is retried when `errorStrategy 'retry'` is set.",
    "",
    "```groovy",
    "errorStrategy 'retry'",
    "maxRetries 3",
    "```",
  ].join("\n"),

  tag: [
    "**tag** — Nextflow process directive",
    "",
    "Assigns a label to the process execution for logging and tracing.",
    "",
    "```groovy",
    'tag "${sample_id}"',
    "```",
  ].join("\n"),

  label: [
    "**label** — Nextflow process directive",
    "",
    "Assigns a label for resource allocation via `withLabel` in config.",
    "",
    "```groovy",
    "label 'big_mem'",
    "",
    "// In nextflow.config:",
    "process { withLabel: 'big_mem' { memory = '32 GB' } }",
    "```",
  ].join("\n"),

  executor: [
    "**executor** — Nextflow process directive",
    "",
    "Specifies the execution platform: `local`, `slurm`, `sge`, `lsf`, `awsbatch`, `google-lifesciences`, `k8s`.",
    "",
    "```groovy",
    "executor 'slurm'",
    "```",
  ].join("\n"),

  queue: [
    "**queue** — Nextflow process directive",
    "",
    "Specifies the cluster queue to submit the process to.",
    "",
    "```groovy",
    "queue 'long'",
    "```",
  ].join("\n"),

  conda: [
    "**conda** — Nextflow process directive",
    "",
    "Specifies a Conda environment or package(s) for the process.",
    "",
    "```groovy",
    "conda 'bioconda::samtools=1.15'",
    "conda '/path/to/environment.yml'",
    "```",
  ].join("\n"),

  module: [
    "**module** — Nextflow process directive",
    "",
    "Loads an environment module before process execution.",
    "",
    "```groovy",
    "module 'samtools/1.15'",
    "module 'bwa/0.7.17:samtools/1.15'",
    "```",
  ].join("\n"),

  collect: [
    "**collect** — Nextflow channel operator",
    "",
    "Collects all items from a channel into a single list.",
    "",
    "```groovy",
    "channel.collect()",
    "channel.collect { it.name }",
    "```",
  ].join("\n"),

  map: [
    "**map** — Nextflow channel operator",
    "",
    "Transforms each item in the channel.",
    "",
    "```groovy",
    "channel.map { it * 2 }",
    "channel.map { id, file -> [id, file.name] }",
    "```",
  ].join("\n"),

  flatMap: [
    "**flatMap** — Nextflow channel operator",
    "",
    "Transforms and flattens items in the channel.",
    "",
    "```groovy",
    "channel.flatMap { it.split('\\n') }",
    "```",
  ].join("\n"),

  filter: [
    "**filter** — Nextflow channel operator",
    "",
    "Emits only items matching the condition.",
    "",
    "```groovy",
    "channel.filter { it > 10 }",
    "channel.filter(Number)",
    "```",
  ].join("\n"),

  groupTuple: [
    "**groupTuple** — Nextflow channel operator",
    "",
    "Groups tuples sharing the same key.",
    "",
    "```groovy",
    "channel.groupTuple()         // groups by first element",
    "channel.groupTuple(by: 1)    // groups by second element",
    "```",
  ].join("\n"),

  join: [
    "**join** — Nextflow channel operator",
    "",
    "Joins two channels by matching key.",
    "",
    "```groovy",
    "left.join(right)",
    "left.join(right, by: 0)",
    "```",
  ].join("\n"),

  branch: [
    "**branch** — Nextflow channel operator",
    "",
    "Splits a channel into multiple named branches based on conditions.",
    "",
    "```groovy",
    "channel.branch {",
    "  small: it < 10",
    "  large: it >= 10",
    "}",
    "```",
  ].join("\n"),

  mix: [
    "**mix** — Nextflow channel operator",
    "",
    "Merges items from two or more channels into a single channel.",
    "",
    "```groovy",
    "ch1.mix(ch2, ch3)",
    "```",
  ].join("\n"),

  concat: [
    "**concat** — Nextflow channel operator",
    "",
    "Concatenates channels preserving emission order (ch1 first, then ch2).",
    "",
    "```groovy",
    "ch1.concat(ch2)",
    "```",
  ].join("\n"),

  first: [
    "**first** — Nextflow channel operator",
    "",
    "Emits only the first item from the channel.",
    "",
    "```groovy",
    "channel.first()",
    "```",
  ].join("\n"),

  take: [
    "**take** — Nextflow channel operator / workflow input",
    "",
    "As operator: emits the first N items. As workflow block: declares named inputs.",
    "",
    "```groovy",
    "channel.take(5)",
    "",
    "// In workflow:",
    "take:",
    "  reads_ch",
    "```",
  ].join("\n"),

  view: [
    "**view** — Nextflow channel operator",
    "",
    "Prints each item to stdout for debugging.",
    "",
    "```groovy",
    "channel.view()",
    'channel.view { "Item: $it" }',
    "```",
  ].join("\n"),

  ifEmpty: [
    "**ifEmpty** — Nextflow channel operator",
    "",
    "Emits a default value when the channel is empty.",
    "",
    "```groovy",
    'channel.ifEmpty("no data")',
    'channel.ifEmpty { error "Channel is empty" }',
    "```",
  ].join("\n"),

  combine: [
    "**combine** — Nextflow channel operator",
    "",
    "Emits the Cartesian product of two channels.",
    "",
    "```groovy",
    "ch1.combine(ch2)",
    "ch1.combine(ch2, by: 0)  // match by key",
    "```",
  ].join("\n"),

  splitCsv: [
    "**splitCsv** — Nextflow channel operator",
    "",
    "Parses CSV content and emits each row.",
    "",
    "```groovy",
    "channel.splitCsv(header: true, sep: ',')",
    "```",
  ].join("\n"),

  splitFastq: [
    "**splitFastq** — Nextflow channel operator",
    "",
    "Splits FASTQ files into chunks.",
    "",
    "```groovy",
    "channel.splitFastq(by: 1_000_000, pe: true)",
    "```",
  ].join("\n"),

  splitText: [
    "**splitText** — Nextflow channel operator",
    "",
    "Splits text content into chunks of lines.",
    "",
    "```groovy",
    "channel.splitText(by: 100)",
    "```",
  ].join("\n"),

  toList: [
    "**toList** — Nextflow channel operator",
    "",
    "Collects all items into a single list (alias for `collect`).",
    "",
    "```groovy",
    "channel.toList()",
    "```",
  ].join("\n"),

  unique: [
    "**unique** — Nextflow channel operator",
    "",
    "Emits only unique items from the channel.",
    "",
    "```groovy",
    "channel.unique()",
    "channel.unique { it.name }",
    "```",
  ].join("\n"),

  distinct: [
    "**distinct** — Nextflow channel operator",
    "",
    "Emits only consecutive distinct items (removes consecutive duplicates).",
    "",
    "```groovy",
    "channel.distinct()",
    "```",
  ].join("\n"),

  set: [
    "**set** — Nextflow channel operator",
    "",
    "Assigns the channel to a variable.",
    "",
    "```groovy",
    "channel.set { result_ch }",
    "```",
  ].join("\n"),

  cross: [
    "**cross** — Nextflow channel operator",
    "",
    "Emits pairs of matching items from two channels based on key.",
    "",
    "```groovy",
    "ch1.cross(ch2)",
    "```",
  ].join("\n"),
};

/**
 * Hover documentation for core Groovy language keywords.
 */
export const groovyHoverData: Record<string, string> = {
  def: [
    "**def** — Groovy",
    "",
    "Declares a variable or method with dynamic typing.",
    "",
    "```groovy",
    'def greeting = "Hello"',
    "def add(a, b) { a + b }",
    "```",
  ].join("\n"),

  class: [
    "**class** — Groovy",
    "",
    "Defines a new class.",
    "",
    "```groovy",
    "class Person {",
    "  String name",
    "  int age",
    "}",
    "```",
  ].join("\n"),

  interface: [
    "**interface** — Groovy",
    "",
    "Defines a contract that classes must implement.",
    "",
    "```groovy",
    "interface Greetable {",
    "  String greet()",
    "}",
    "```",
  ].join("\n"),

  enum: [
    "**enum** — Groovy",
    "",
    "Defines a fixed set of constants.",
    "",
    "```groovy",
    "enum Color { RED, GREEN, BLUE }",
    "```",
  ].join("\n"),

  abstract: [
    "**abstract** — Groovy",
    "",
    "Declares a class or method that cannot be instantiated directly and must be subclassed.",
  ].join("\n"),

  extends: [
    "**extends** — Groovy",
    "",
    "Indicates that a class inherits from a parent class.",
    "",
    "```groovy",
    "class Dog extends Animal { }",
    "```",
  ].join("\n"),

  implements: [
    "**implements** — Groovy",
    "",
    "Indicates that a class implements one or more interfaces.",
    "",
    "```groovy",
    "class Cat implements Serializable, Comparable { }",
    "```",
  ].join("\n"),

  for: [
    "**for** — Groovy",
    "",
    "Loop construct. Supports classic, range, and each-style iteration.",
    "",
    "```groovy",
    "for (i in 0..9) { println i }",
    "for (item in list) { println item }",
    "```",
  ].join("\n"),

  while: [
    "**while** — Groovy",
    "",
    "Repeats a block while the condition is `true`.",
    "",
    "```groovy",
    "while (count < 10) { count++ }",
    "```",
  ].join("\n"),

  switch: [
    "**switch** — Groovy",
    "",
    "Multi-branch conditional. Supports types, ranges, regex, and closures as case values.",
    "",
    "```groovy",
    "switch (x) {",
    "  case 1..10: println 'small'; break",
    "  case ~/\\d+/: println 'number'; break",
    "  default: println 'other'",
    "}",
    "```",
  ].join("\n"),

  case: [
    "**case** — Groovy",
    "",
    "A branch inside a `switch` statement. Matches values, types, ranges, or regex.",
  ].join("\n"),

  return: [
    "**return** — Groovy",
    "",
    "Returns a value from a method. In Groovy, the last expression is implicitly returned.",
    "",
    "```groovy",
    "def add(a, b) { return a + b }",
    "def add(a, b) { a + b }  // implicit return",
    "```",
  ].join("\n"),

  try: [
    "**try** — Groovy",
    "",
    "Starts an exception handling block.",
    "",
    "```groovy",
    "try {",
    "  riskyOperation()",
    "} catch (IOException e) {",
    "  log.error(e.message)",
    "} finally {",
    "  cleanup()",
    "}",
    "```",
  ].join("\n"),

  catch: [
    "**catch** — Groovy",
    "",
    "Catches exceptions thrown in the `try` block.",
  ].join("\n"),

  finally: [
    "**finally** — Groovy",
    "",
    "Block that always executes after `try`/`catch`, regardless of exceptions.",
  ].join("\n"),

  throw: [
    "**throw** — Groovy",
    "",
    "Throws an exception.",
    "",
    "```groovy",
    'throw new IllegalArgumentException("Invalid value")',
    "```",
  ].join("\n"),

  assert: [
    "**assert** — Groovy",
    "",
    "Asserts that a condition is true. Provides a power assertion message on failure.",
    "",
    "```groovy",
    "assert list.size() == 3",
    'assert name != null : "Name is required"',
    "```",
  ].join("\n"),

  in: [
    "**in** — Groovy",
    "",
    "Membership test or iteration operator.",
    "",
    "```groovy",
    "if (x in [1, 2, 3]) { ... }",
    "for (item in collection) { ... }",
    "```",
  ].join("\n"),

  as: [
    "**as** — Groovy",
    "",
    "Type coercion operator.",
    "",
    "```groovy",
    'def num = "42" as Integer',
    "def list = [1, 2, 3] as Set",
    "```",
  ].join("\n"),

  instanceof: [
    "**instanceof** — Groovy",
    "",
    "Checks whether an object is an instance of a specific type.",
    "",
    "```groovy",
    "if (obj instanceof String) { ... }",
    "```",
  ].join("\n"),

  static: [
    "**static** — Groovy",
    "",
    "Declares a class-level field or method (not tied to an instance).",
  ].join("\n"),

  final: [
    "**final** — Groovy",
    "",
    "Declares a constant (variable cannot be reassigned, class cannot be subclassed, method cannot be overridden).",
  ].join("\n"),

  void: ["**void** — Groovy", "", "Indicates a method returns no value."].join(
    "\n"
  ),

  import: [
    "**import** — Groovy",
    "",
    "Imports a class or package for use.",
    "",
    "```groovy",
    "import java.nio.file.Path",
    "import groovy.json.JsonSlurper",
    "```",
  ].join("\n"),

  new: [
    "**new** — Groovy",
    "",
    "Creates a new instance of a class.",
    "",
    "```groovy",
    "def list = new ArrayList()",
    "def file = new File('/path/to/file')",
    "```",
  ].join("\n"),

  this: [
    "**this** — Groovy",
    "",
    "Refers to the current object instance.",
  ].join("\n"),

  super: [
    "**super** — Groovy",
    "",
    "Refers to the parent class. Used to call parent constructors or methods.",
  ].join("\n"),
};

/**
 * Hover documentation for Groovy operators.
 */
export const operatorHoverData: Record<string, string> = {
  "?.": [
    "**?.** — Safe navigation operator (Groovy)",
    "",
    "Avoids `NullPointerException` by returning `null` if the receiver is `null`.",
    "",
    "```groovy",
    "def name = person?.address?.city",
    "// Returns null instead of throwing NPE",
    "```",
  ].join("\n"),

  "?:": [
    "**?:** — Elvis operator (Groovy)",
    "",
    "Returns the left operand if truthy, otherwise the right operand.",
    "",
    "```groovy",
    'def name = user.name ?: "Anonymous"',
    "```",
  ].join("\n"),

  "*.": [
    "***.** — Spread operator (Groovy)",
    "",
    "Invokes a method or property on each item in a collection.",
    "",
    "```groovy",
    "def names = people*.name",
    "// Equivalent to: people.collect { it.name }",
    "```",
  ].join("\n"),

  "<=>": [
    "**<=>** — Spaceship operator (Groovy)",
    "",
    "Compares two values. Returns `-1`, `0`, or `1`.",
    "",
    "```groovy",
    "assert (1 <=> 2) == -1",
    "list.sort { a, b -> a <=> b }",
    "```",
  ].join("\n"),

  "=~": [
    "**=~** — Regex find operator (Groovy)",
    "",
    "Creates a `Matcher` from a regex pattern. Truthy if pattern is found.",
    "",
    "```groovy",
    'if (text =~ /\\d+/) { println "Contains numbers" }',
    "```",
  ].join("\n"),

  "==~": [
    "**==~** — Regex match operator (Groovy)",
    "",
    "Returns `true` if the entire string matches the regex pattern.",
    "",
    "```groovy",
    'assert "12345" ==~ /\\d+/',
    "```",
  ].join("\n"),

  "..": [
    "**..** — Range operator (Groovy)",
    "",
    "Creates an inclusive range.",
    "",
    "```groovy",
    "def range = 1..10       // [1, 2, ..., 10]",
    "def letters = 'a'..'z'",
    "```",
  ].join("\n"),

  "..<": [
    "**..<** — Exclusive range operator (Groovy)",
    "",
    "Creates a range excluding the upper bound.",
    "",
    "```groovy",
    "def range = 0..<10  // [0, 1, ..., 9]",
    "```",
  ].join("\n"),

  "**": [
    "****  — Power operator (Groovy)",
    "",
    "Raises the left operand to the power of the right.",
    "",
    "```groovy",
    "assert 2**10 == 1024",
    "```",
  ].join("\n"),

  "<<": [
    "**<<** — Left shift / append operator (Groovy)",
    "",
    "Appends to a collection or `StringBuilder`. Also a bitwise left shift.",
    "",
    "```groovy",
    "def list = [1, 2]",
    "list << 3          // [1, 2, 3]",
    "```",
  ].join("\n"),
};

/**
 * Combined lookup: merges all hover maps.
 * Nextflow entries take precedence over Groovy entries for overlapping keys.
 */
export const allHoverData: Record<string, string> = {
  ...groovyHoverData,
  ...nextflowHoverData,
};
